/*
  global.js - NOTFORTOMORROW OS2.0 shared core (~3 KB target)
  Loaded on every page (type="module" defer). Provides the ONE shared primitives:
    1. <nft-dialog> accessible-dialog primitive (cart drawer, mobile menu, lightbox, banner).
    2. announce(msg) - persistent aria-live announcer.
    3. small lazy/intersection helpers.
  No framework. No legacy island globals. Every overlay in the theme uses <nft-dialog>;
  no component hand-rolls a focus trap.
*/

/* ---- aria-live announcer ------------------------------------------------ */
export function announce(message, assertive = false) {
  const el = document.getElementById('nft-announcer');
  if (!el) return;
  el.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
  // Clear then set so repeated identical messages are re-announced.
  el.textContent = '';
  requestAnimationFrame(() => { el.textContent = message; });
}

/* ---- intersection helper ------------------------------------------------ */
export function onEnterViewport(el, cb, options = { rootMargin: '0px', threshold: 0 }) {
  if (!('IntersectionObserver' in window)) { cb(); return () => {}; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) cb(e); });
  }, options);
  io.observe(el);
  return () => io.disconnect();
}

/* ---- <nft-dialog> shared accessible dialog primitive -------------------- */
class NftDialog extends HTMLElement {
  connectedCallback() {
    this._lastFocus = null;
    this._onKeydown = this._onKeydown.bind(this);
    // Triggers anywhere: [data-dialog-open="<id>"] opens, [data-dialog-close] closes.
    if (this.id) {
      document.querySelectorAll(`[data-dialog-open="${this.id}"]`).forEach((btn) => {
        // preventDefault so an <a> trigger (e.g. the cart icon linking to /cart as the
        // no-JS fallback) opens the drawer instead of navigating away. JS off => link still works.
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
    }
    this.querySelectorAll('[data-dialog-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.addEventListener('click', (e) => { if (e.target === this) this.close(); }); // backdrop
  }

  open() {
    this._lastFocus = document.activeElement;
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeydown);
    // TODO: set inert/aria-hidden on the rest of the page (siblings of this dialog).
    const focusable = this._focusable();
    (focusable[0] || this).focus();
    this.dispatchEvent(new CustomEvent('nft:dialog-open', { bubbles: true }));
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this._onKeydown);
    if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    this.dispatchEvent(new CustomEvent('nft:dialog-close', { bubbles: true }));
  }

  _focusable() {
    return Array.from(this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    ));
  }

  _onKeydown(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab') return;
    // Focus trap.
    const f = this._focusable();
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}
if (!customElements.get('nft-dialog')) customElements.define('nft-dialog', NftDialog);

/* ---- global scroll-reveal ----------------------------------------------- */
/* Covers shared [data-reveal]/[data-stagger] elements the per-section observers miss
   (cart head, footer, etc.). Above-fold elements reveal on load; reduced-motion or no
   IntersectionObserver reveals everything; a 1.2s safety net reveals anything still
   hidden so content is never stuck invisible. Adding 'in' twice is harmless. */
(function initReveal() {
  const reveal = (el) => el.classList.add('in');
  const start = () => {
    const els = document.querySelectorAll('[data-reveal], [data-stagger]');
    if (!els.length) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) { els.forEach(reveal); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { reveal(e.target); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => { if (!el.classList.contains('in')) io.observe(el); });
    setTimeout(() => {
      document.querySelectorAll('[data-reveal]:not(.in), [data-stagger]:not(.in)').forEach(reveal);
    }, 1200);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Expose helpers for non-module inline use if ever needed (single namespaced object). */
window.nft = Object.assign(window.nft || {}, { announce, onEnterViewport });
